import {
  ICustomerLicenseFacilityRow,
  ICustomerLicenseRates,
  ICustomerLicenseUsage,
  ILocalityLicenseActivityLog,
  IUpdateCustomerLicenseRatesRequest,
} from 'src/generated-types/customer-license'
import { PaginatedResultList } from 'src/generated-types/paginated-result-list'
import { axios } from 'src/axios'
import { downloadFileLocal } from 'src/utils/downloadFile'
import { apiConsts } from './apiConsts'
import { useGet } from './reactQuery'
import { useMutation, useQueryClient } from 'react-query'

export const licenseEndpoints = {
  getLicenseUsage: () => 'admin/customers/license-usage',
  getLicenseFacilities: (customerId: EntityId) => `admin/customers/${customerId}/license-facilities`,
  getLicenseRates: (customerId: EntityId) => `admin/customers/${customerId}/license-rates`,
  getLocalityLicenseActiveInactiveLogs: (customerId: EntityId, localityId: number) =>
    `admin/customers/${customerId}/localities/${localityId}/license-active-inactive-logs`,
  postLicenseBillingPdf: () => 'admin/customers/license-billing-pdf',
}

export type LicenseUsageQueryParams = {
  page: number
  pageSize: number
  isActive: boolean
  search?: string
  /** Inclusive billing period; when both set, API returns prorated period totals in monthlyTotal. */
  from?: string
  to?: string
}

/**
 * Paginated customer license usage (localities, service stations, storages + estimated monthly totals).
 */
export const useGetCustomerLicenseUsage = (
  params: LicenseUsageQueryParams,
  config?: { enabled?: boolean },
) => {
  const queryParams = {
    page: params.page,
    pageSize: params.pageSize,
    isActive: params.isActive,
    ...(params.search?.trim() ? { search: params.search.trim() } : {}),
    ...(params.from && params.to ? { from: params.from, to: params.to } : {}),
  }
  return useGet<PaginatedResultList<ICustomerLicenseUsage>>(
    licenseEndpoints.getLicenseUsage(),
    queryParams,
    { enabled: config?.enabled ?? true },
  )
}

export type LicenseFacilitiesDateRange = {
  from: Date | null
  to: Date | null
}

/**
 * Localities, service stations, and storages for one customer (license breakdown table).
 * When both `from` and `to` are set, the API returns only facilities whose CreatedAt falls in that range (inclusive calendar days).
 */
export const useGetCustomerLicenseFacilities = (
  customerId: EntityId | null | undefined,
  dateRange?: LicenseFacilitiesDateRange,
) => {
  const params =
    dateRange?.from && dateRange?.to
      ? { from: dateRange.from.toISOString(), to: dateRange.to.toISOString() }
      : undefined
  return useGet<ICustomerLicenseFacilityRow[]>(
    customerId != null ? licenseEndpoints.getLicenseFacilities(customerId) : '',
    params,
    { enabled: customerId != null },
  )
}

/** Fetch all rows for the sidebar in one request (super-admin list). */
export const defaultLicenseUsagePageSize = apiConsts.Max

export const useGetCustomerLicenseRates = (customerId: EntityId | null | undefined, config?: { enabled?: boolean }) => {
  const url = customerId != null ? licenseEndpoints.getLicenseRates(customerId) : ''
  return useGet<ICustomerLicenseRates>(url, undefined, {
    enabled: (config?.enabled ?? true) && !!url,
  })
}

export const updateCustomerLicenseRates = async (
  customerId: EntityId,
  body: IUpdateCustomerLicenseRatesRequest,
): Promise<void> => {
  await axios.put(licenseEndpoints.getLicenseRates(customerId), body)
}

export const useUpdateCustomerLicenseRates = () => {
  const qc = useQueryClient()
  return useMutation<void, Error, { customerId: EntityId; body: IUpdateCustomerLicenseRatesRequest }>(
    ({ customerId, body }) => updateCustomerLicenseRates(customerId, body),
    {
      onSuccess: (_, { customerId }) => {
        void qc.invalidateQueries(licenseEndpoints.getLicenseUsage())
        void qc.invalidateQueries(licenseEndpoints.getLicenseFacilities(customerId))
        void qc.invalidateQueries(licenseEndpoints.getLicenseRates(customerId))
      },
    },
  )
}

export const useGetLocalityLicenseActiveInactiveLogs = (
  customerId: EntityId | null | undefined,
  localityId: number | null | undefined,
  config?: { enabled?: boolean },
) => {
  const url =
    customerId != null && localityId != null && localityId > 0
      ? licenseEndpoints.getLocalityLicenseActiveInactiveLogs(customerId, localityId)
      : ''
  const enabled = (config?.enabled ?? true) && !!url
  return useGet<ILocalityLicenseActivityLog[]>(url, undefined, { enabled })
}

export type LicenseBillingPdfPayload = {
  from: string
  to: string
  isActive: boolean
  search?: string
  customerIds: number[]
}

/**
 * Downloads PDF: customer license summary table + facilities per customer for the given period.
 */
export const downloadLicenseBillingPdf = async (
  payload: LicenseBillingPdfPayload,
  fileNameBase?: string,
): Promise<void> => {
  const res = await axios.post(licenseEndpoints.postLicenseBillingPdf(), payload, { responseType: 'arraybuffer' })
  const stamp = new Date().toISOString().slice(0, 10)
  downloadFileLocal(res, fileNameBase ?? `license-billing-${stamp}`)
}
